import { differenceInHours } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

export interface SyncStatus {
  status: 'connected' | 'not_connected' | 'unknown';
  message: string;
  variant: 'default' | 'destructive' | 'secondary';
  daysSinceLastSync: number | null;
  hoursSinceLastSync: number | null;
  syncHealth: 'excellent' | 'good' | 'warning' | 'poor';
  hasTokenIssue?: boolean;
  tokenNote?: string;
}

/**
 * Get the actual last sync time by checking both connection data and metrics data
 * This provides a more accurate picture of when data was last successfully synced
 */
export async function getActualLastSyncTime(connection: any, userId: string): Promise<string | null> {
  console.log('🔍 getActualLastSyncTime called for connection:', connection.id, connection.provider);
  
  // Always fetch the latest connection data from the database to get the most up-to-date last_successful_sync
  try {
    const { data: latestConnection, error: connectionError } = await supabase
      .from('wearable_connections')
      .select('last_successful_sync')
      .eq('id', connection.id)
      .single();

    if (connectionError) {
      console.error('Error fetching latest connection data:', connectionError);
    } else if (latestConnection?.last_successful_sync) {
      console.log('✅ Found latest last_successful_sync from database:', latestConnection.last_successful_sync);
      return latestConnection.last_successful_sync;
    } else {
      console.log('⚠️ No last_successful_sync found in database for connection:', connection.id);
    }
  } catch (error) {
    console.error('Error fetching latest connection data:', error);
  }

  // Fallback: check the most recent data in the metrics table
  try {
    const { data: metrics, error } = await supabase
      .from('wearable_metrics')
      .select('record_date, created_at')
      .eq('user_id', userId)
      .eq('provider', connection.provider)
      .order('record_date', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error fetching metrics for last sync time:', error);
      return null;
    }

    if (metrics && metrics.length > 0) {
      // Use the created_at timestamp of the most recent metric as the last sync time
      return metrics[0].created_at;
    }
  } catch (error) {
    console.error('Error determining last sync time:', error);
  }

  return null;
}

/**
 * Determine sync status based on last successful sync time with more sophisticated logic
 * 
 * Status Rules:
 * - Connected: Last sync within 24 hours OR (within 3 days AND user has recent data)
 * - Not Connected: No sync for > 3 days OR no sync data available
 * - Unknown: No sync data available
 * 
 * Sync Health Levels:
 * - Excellent: < 12 hours
 * - Good: 12-24 hours  
 * - Warning: 1-3 days
 * - Poor: > 3 days
 * 
 * IMPORTANT: Token expiration is handled automatically by the backend cron job,
 * so we prioritize actual sync health over token status to avoid false alarms.
 */
export function getSyncStatus(lastSuccessfulSync: string | null, connection?: any): SyncStatus {
  if (!lastSuccessfulSync) {
    return {
      status: 'not_connected',
      message: 'Not Connected',
      variant: 'destructive',
      daysSinceLastSync: null,
      hoursSinceLastSync: null,
      syncHealth: 'poor'
    };
  }

  const lastSync = new Date(lastSuccessfulSync);
  const now = new Date();
  const hoursSinceLastSync = differenceInHours(now, lastSync);
  const daysSinceLastSync = Math.floor(hoursSinceLastSync / 24);

  // Check token health if connection data is provided
  // Only consider it a token issue if the database explicitly marks it as expired or there are failures
  // Don't just check expires_at since the backend handles refresh automatically
  const hasExpiredToken = connection && (
    connection.token_health_status === 'expired' ||
    ((connection.refresh_failure_count || 0) >= 3)
  );

  // Determine sync health level
  let syncHealth: 'excellent' | 'good' | 'warning' | 'poor';
  if (hoursSinceLastSync < 12) {
    syncHealth = 'excellent';
  } else if (hoursSinceLastSync < 24) {
    syncHealth = 'good';
  } else if (daysSinceLastSync < 3) {
    syncHealth = 'warning';
  } else {
    syncHealth = 'poor';
  }

  // Determine connection status - PRIORITIZE ACTUAL SYNC HEALTH over token status
  // The backend cron job handles token refresh automatically, so we focus on whether data is actually syncing
  
  if (daysSinceLastSync < 1) {
    // Within 24 hours - definitely connected regardless of token status
    // Token expiration is handled automatically by the backend
    const message = syncHealth === 'excellent' 
      ? 'Connected' 
      : `Connected (${hoursSinceLastSync}h ago)`;
    
    return {
      status: 'connected',
      message,
      variant: 'default',
      daysSinceLastSync,
      hoursSinceLastSync,
      syncHealth,
      hasTokenIssue: hasExpiredToken, // Track token issue but don't change status
      tokenNote: hasExpiredToken ? 'Token refreshing automatically' : undefined
    };
  } else if (daysSinceLastSync <= 3) {
    // 1-3 days - still connected but with warning
    return {
      status: 'connected',
      message: `Connected (${daysSinceLastSync} days ago)`,
      variant: 'default',
      daysSinceLastSync,
      hoursSinceLastSync,
      syncHealth,
      hasTokenIssue: hasExpiredToken,
      tokenNote: hasExpiredToken ? 'Token refreshing automatically' : undefined
    };
  } else {
    // > 3 days - not connected (this is the real issue, not token expiration)
    return {
      status: 'not_connected',
      message: `Not Connected (${daysSinceLastSync} days ago)`,
      variant: 'destructive',
      daysSinceLastSync,
      hoursSinceLastSync,
      syncHealth,
      hasTokenIssue: hasExpiredToken
    };
  }
}

/**
 * Get sync status icon based on sync status
 */
export function getSyncStatusIcon(status: SyncStatus) {
  switch (status.status) {
    case 'connected':
      return 'check-circle';
    case 'not_connected':
      return 'x-circle';
    case 'unknown':
      return 'help-circle';
    default:
      return 'help-circle';
  }
}

/**
 * Get sync status color classes based on sync status
 */
export function getSyncStatusColor(status: SyncStatus) {
  switch (status.status) {
    case 'connected':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'not_connected':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'unknown':
      return 'text-gray-600 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

/**
 * Get sync health indicator color
 */
export function getSyncHealthColor(syncHealth: SyncStatus['syncHealth']) {
  switch (syncHealth) {
    case 'excellent':
      return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    case 'good':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'warning':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'poor':
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
} 